/**
 * CandidateSourcesPanel — graph-discovered sources awaiting approval.
 * Renders only when at least one candidate exists. Mounts above the source table in IntelSources.tsx.
 * Template example: NextAdmin `(dashboard)/(home)/_component/top-products` (card of rows with a leading tile and
 * trailing actions).
 */
import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { GitBranch } from 'lucide-react';
import {
  listIntelSourcesOptions,
  reviewIntelSourceApprovalMutation,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import type { IntelSource } from '../../../types/intel-source.js';
import { Can } from '../../../components/auth/Can.js';
import { Badge } from '../../../components/ui/Badge.js';
import { Button } from '../../../components/ui/Button.js';
import { Callout } from '../../../components/ui/Callout.js';
import { Card } from '../../../components/ui/Card.js';
import { InlineConfirm } from '../../../components/ui/InlineConfirm.js';
import { useToast } from '../../../components/ui/Toast.js';
import { PlatGlyph } from './PlatGlyph.js';
import { candidateSourcesQuery, useIntelSourceInvalidation } from './intel-source-queries.js';

interface CandidateRowProps {
  source: IntelSource;
}

function CandidateRow({ source }: CandidateRowProps) {
  const invalidate = useIntelSourceInvalidation();
  const { notify } = useToast();
  const [confirmReject, setConfirmReject] = useState(false);

  const approveMut = useMutation({
    ...reviewIntelSourceApprovalMutation(),
    onSuccess: () => {
      invalidate();
      notify('Approved. A monitor was created.');
    },
    onError: () => notify('Could not approve the source. Try again.', 'danger'),
  });

  const rejectMut = useMutation({
    ...reviewIntelSourceApprovalMutation(),
    onSuccess: () => {
      invalidate();
      setConfirmReject(false);
      notify('Source rejected.');
    },
    onError: () => {
      setConfirmReject(false);
      notify('Could not reject the source. Try again.', 'danger');
    },
  });

  const isPending = approveMut.isPending || rejectMut.isPending;

  return (
    <div className="flex flex-wrap items-center gap-3 py-3 first:pt-0 last:pb-0">
      <PlatGlyph platform={source.platform} size={32} radius={8} />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2">
          <span className="truncate text-sm font-medium text-heading">{source.name ?? source.handle}</span>
          <span className="font-mono text-xs text-muted">@{source.handle}</span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-muted">
          {source.discoveredVia != null && <span>Found through graph expansion</span>}
          {source.discoveryDepth != null && <span>Depth {source.discoveryDepth}</span>}
          {source.platform && <span className="capitalize">{source.platform}</span>}
        </div>
      </div>

      <Can policy="CanManageAgents">
        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="sm"
            variant="primary"
            disabled={isPending}
            loading={approveMut.isPending}
            onClick={() =>
              approveMut.mutate({
                path: { id: source.id },
                body: { action: 'approve', createMonitor: true, cadence: '@daily', transcriptEnabled: false },
              })
            }
          >
            Approve
          </Button>
          {confirmReject ? (
            <InlineConfirm
              message="Reject this candidate source?"
              confirmLabel="Reject"
              confirmVariant="warning"
              loading={rejectMut.isPending}
              onConfirm={() => rejectMut.mutate({ path: { id: source.id }, body: { action: 'reject' } })}
              onCancel={() => setConfirmReject(false)}
            />
          ) : (
            <Button variant="ghost" size="sm" disabled={isPending} onClick={() => setConfirmReject(true)}>
              Reject
            </Button>
          )}
        </div>
      </Can>
    </div>
  );
}

export const CandidateSourcesPanel: React.FC = () => {
  const { data, isError } = useQuery({
    ...listIntelSourcesOptions(candidateSourcesQuery()),
    staleTime: 30_000,
  });

  const candidates: IntelSource[] = data?.data ?? [];

  if (isError) {
    return (
      <Callout tone="danger" title="Could not load graph-discovered sources">
        Candidate sources could not be loaded. Reload the page to try again.
      </Callout>
    );
  }

  // Never renders an empty block.
  if (candidates.length === 0) return null;

  return (
    <Card
      title="Graph-discovered sources awaiting approval"
      actions={
        <Badge variant="warning" size="sm">
          <GitBranch size={12} className="mr-1 inline-block" aria-hidden="true" />
          {candidates.length}
        </Badge>
      }
    >
      <div className="divide-y divide-border">
        {candidates.map((source) => (
          <CandidateRow key={source.id} source={source} />
        ))}
      </div>
      <p className="mt-4 text-xs text-muted">
        Approving creates an active monitor. Candidates are never fetched until approved.
      </p>
    </Card>
  );
};
